import React, { useEffect, useState } from "react";
import { Download, Printer, Upload } from 'lucide-react';
import apiService from "../../../config";
import { useSignature } from '../../../DigitalSignature/UseSignatue';
import SignaturePad from '../../../DigitalSignature/SignaturePad';

const HighwayAnusuchi1 = ({ projectId = null }) => {
  const { uploadStatus, uploadError, selectedSignDocument, resetSignatureState, signDocument } = useSignature();
  const [data, setData] = useState([]);
  const [editMode, setEditMode] = useState(false); // Keep it as false to disable edit mode
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [locationInfo, setLocationInfo] = useState({});
  const [debugInfo, setDebugInfo] = useState(null);
  const [proposalNo, setProposalNo] = useState('');
  const [proposalData, setProposalData] = useState(null);
  const [regionalInchargeName, setRegionalInchargeName] = useState("");
  const [regionalInchargeDesignation, setRegionalInchargeDesignation] = useState("");
  const [InchargeName, setInchargeName] = useState("");
  const [InchargeDesignation, setInchargeDesignation] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);
  const userName = userData?.full_name || 'N/A';
  const userDesi = userData?.designation || 'N/A';

  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfForSigning, setPdfForSigning] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Helper function to find proposalId in the project object
  const findProposalId = (projectObj) => {
    if (!projectObj) return null;

    const possibleKeys = [
      'acquisition_proposal_no',
      'land_acquisition_no',
      'proposal_id',
      'proposalId',
      'proposal',
      'id',
      'project_id'
    ];

    for (const key of possibleKeys) {
      if (projectObj[key] !== undefined && projectObj[key] !== null && projectObj[key] !== '') {
        // console.log(`Found proposalId in key: ${key}, value: ${projectObj[key]}`);
        return projectObj[key];
      }
    }

    return null;
  };

  const fetchLandHoldings = async () => {
    setFetching(true);
    try {
      setLoading(true);
      setError(null);

      const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
      const projectIdFromStorage = selectedProject?.id || selectedProject?.project_id;
      const proposalId = localStorage.getItem('currentProposalId') || findProposalId(selectedProject);
      const currentProjectId = projectId || projectIdFromStorage;

      // Get acquisition_regional_incharge_name from selectedProposal
      const storedProposal = JSON.parse(localStorage.getItem('selectedProposal') || '{}');
      if (storedProposal?.acquisition_regional_incharge_name) {
        setRegionalInchargeName(storedProposal.acquisition_regional_incharge_name);
      }
      if (storedProposal?.acquisition_regional_incharge_designation) {
        setRegionalInchargeDesignation(storedProposal.acquisition_regional_incharge_designation);
      }
      if (storedProposal?.acquisition_incharge_designation) {
        setInchargeDesignation(storedProposal.acquisition_incharge_designation);
      }
      if (storedProposal?.acquisition_incharge_name) {
        setInchargeName(storedProposal.acquisition_incharge_name);
      }

      if (!currentProjectId || !proposalId) {
        const missingParams = [];
        if (!currentProjectId) missingParams.push('projectId');
        if (!proposalId) missingParams.push('proposalId');

        setError(`Missing required parameters: ${missingParams.join(', ')}. Please select a project first.`);
        setLoading(false);
        return;
      }

      const storedLocationInfo = localStorage.getItem('selectedLocationInfo');
      // console.log("-------------------:",storedLocationInfo)
      let apiParams = {
        proposalId: proposalId
      };

      if (storedLocationInfo) {
        const locationData = JSON.parse(storedLocationInfo);
        apiParams = {
          ...apiParams,
          distNo: locationData.district,
          talukaNo: locationData.taluka,
          villageNo: locationData.village
        };
        setLocationInfo(locationData);
      }

      const result = await apiService.getLandHolding(currentProjectId, apiParams);

      setDebugInfo({
        apiResponse: result,
        requestParams: { projectId: currentProjectId, ...apiParams },
        responseStatus: result?.status,
        responseData: result?.data,
        dataType: typeof result?.data,
        dataLength: result?.data?.length,
        dataKeys: result?.data ? Object.keys(result.data) : null,
        rawResponse: result,
        isEmptyString: result === "",
        isNull: result === null,
        isUndefined: result === undefined
      });

      if (!result || result === "" || result === null || result === undefined) {
        setError(`API returned invalid response: ${JSON.stringify(result)}. Check API endpoint and network connection.`);
        return;
      }

      let dataArray = null;
      let responseStatus = result?.status || result?.statusCode || 200;

      if (responseStatus === 200 || responseStatus === "200") {
        // FIXED: Extract proposalData first
        if (result.proposalData && Array.isArray(result.proposalData) && result.proposalData.length > 0) {
          const proposalInfo = result.proposalData[0];
          setProposalNo(proposalInfo.acquisition_proposal_no);
          setProposalData(proposalInfo);
          // console.log('📊 Extracted Proposal No:', proposalInfo.acquisition_proposal_no);
        }

        // Then extract land holdings data
        if (Array.isArray(result.data)) {
          dataArray = result.data;
        } else if (result.data && Array.isArray(result.data.data)) {
          dataArray = result.data.data;
        } else if (result.data && Array.isArray(result.data.items)) {
          dataArray = result.data.items;
        } else if (result.data && Array.isArray(result.data.records)) {
          dataArray = result.data.records;
        } else if (result.data && Array.isArray(result.data.landHoldings)) {
          dataArray = result.data.landHoldings;
        }

        if (dataArray && dataArray.length > 0) {
          const firstRecord = dataArray[0];
          if (!storedLocationInfo && firstRecord) {
            const extractedLocationInfo = {
              projectId: firstRecord.project_id || currentProjectId,
              district: firstRecord.dist_name,
              taluka: firstRecord.tehsil_name || firstRecord.taluka_name,
              village: firstRecord.village_name || firstRecord.VillageName,
              districtNo: firstRecord.dist_no,
              talukaNo: firstRecord.tehsil_no,
              villageNo: firstRecord.village_no
            };
            setLocationInfo(extractedLocationInfo);
            localStorage.setItem('selectedLocationInfo', JSON.stringify(extractedLocationInfo));
          }

          const transformedData = dataArray.map((item, index) => ({
            ...item,
            sr_no: index + 1
          }));

          setData(transformedData);

        } else {
          const errorMsg = result.data
            ? `API returned successful response but no data records found. Response structure: ${JSON.stringify(result.data, null, 2)}`
            : 'API returned empty response';
          setError(errorMsg);
        }
      } else {
        setError(`API returned status ${responseStatus}: ${result?.message || result?.error || 'Unknown error'}. Raw response: ${JSON.stringify(result)}`);
      }
    } catch (err) {
      console.error('❌ Error fetching land holdings:', err);
      setError(`Failed to fetch land holdings data: ${err.message}`);
      setDebugInfo({
        error: err,
        errorMessage: err.message,
        errorStack: err.stack
      });
    } finally {
      setLoading(false);
    }
  };

  console.log("result", proposalData);

  // useEffect to fetch land holding list
  useEffect(() => {
    if (!previewUrl) {
      fetchLandHoldings();
    }
  }, [projectId]);

  // New useEffect to handle posting signed document
  useEffect(() => {
    if (selectedSignDocument) {
      const postSignedPdf = async () => {
        try {
          const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
          const projectId = selectedProject?.id || selectedProject?.project_id;
          const proposalId = localStorage.getItem("currentProposalId");

          if (!projectId || !proposalId) {
            console.error('Missing projectId or proposalId');
            return;
          }

          await apiService.postAddSignaturePDF(
            projectId,
            proposalId,
            "parishisht1",
            selectedSignDocument
          );

          const sectionRes = await apiService.getSectionFile(
            projectId,
            proposalId,
            "parishisht1"
          );

          if (sectionRes?.isSignAvailable && sectionRes.data?.[0]?.file_path) {
            setPreviewUrl(sectionRes.data[0].file_path);
          }
        } catch (error) {
          console.error('Error posting signed PDF:', error);
        }
      };

      postSignedPdf();
    }
  }, [selectedSignDocument]);

  const handleViewSignedPdf = async () => {
    try {
      const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
      const projectId = selectedProject?.id || selectedProject?.project_id;
      const proposalId = localStorage.getItem("currentProposalId");

      if (!projectId || !proposalId) {
        alert("Project or Proposal not found");
        return;
      }

      const res = await apiService.getSectionFile(
        projectId,
        proposalId,
        "parishisht1"
      );

      if (res?.isSignAvailable && res?.data?.[0]?.file_path) {
        setViewerFile(res.data[0].file_path); // same as ViewProposal
        setIsViewerOpen(true);                // same as ViewProposal
      } else {
        alert("Digital signature PDF not available");
      }
    } catch (error) {
      console.error("Error opening signed PDF:", error);
      alert("Unable to open document");
    }
  };

  const handleUploadedFile = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // ✅ open uploaded file in signature modal
    setPdfForSigning(file);
    setShowSignaturePad(true);
  };

  const handlePrint = () => {
    if (previewUrl) {
      // 🔹 Open signed PDF in new tab
      window.open(previewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // ⏬ Fallback: Print HTML if previewUrl not available
    if (!data || data.length === 0) {
      alert("No data to print yet.");
      return;
    }

    const wasEditing = editMode;
    setEditMode(false);

    setTimeout(() => {
      const printContent = document.getElementById("notice-content")?.innerHTML;

      if (!printContent || printContent.trim() === "") {
        alert("Print content is empty.");
        setEditMode(wasEditing);
        return;
      }

      const win = window.open("", "", "height=900,width=1200");
      win.document.write(`<html><head><title>परिशिष्ट-1</title>`);
      win.document.write(`<style>
        @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
      table { border-collapse: collapse; width: 100%; margin-top: 10px; }
      th, td { border: 1px solid black; padding: 4px; text-align: center; }
      h2, h3 { text-align: center; margin: 0; }
      .center-text { text-align: center; }
      .left-text { text-align: left; }
    </style></head><body>`);
      win.document.write(printContent);
      win.document.write("</body></html>");
      win.document.close();
      win.print();

      setEditMode(wasEditing);
    }, 200);
  };

  const handleDownloadWord = () => {
    if (previewUrl) {
      // 🔹 Open same file in new tab but force .doc download
      const link = document.createElement("a");
      link.href = previewUrl;
      link.target = "_blank";
      link.download = "अनुसूची-२.doc";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // ⏬ Fallback: Generate Word from HTML if no previewUrl
    if (loading) {
      alert("Data is still loading. Please wait before downloading.");
      return;
    }

    const wasEditing = editMode;
    setEditMode(false);

    setTimeout(() => {
      const content = document.getElementById("notice-content").innerHTML;

      const header = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' 
            xmlns:w='urn:schemas-microsoft-com:office:word' 
            xmlns='http://www.w3.org/TR/REC-html40'>
      <head><title>अनुसूची-२</title>
      <style>
        body { font-family: 'Mangal', 'Arial', sans-serif; margin: 20mm; }
        table { border-collapse: collapse; width: 100%; margin-top: 10px; }
        th, td { border: 1px solid black; padding: 4px; text-align: center; }
        h2, h3 { text-align: center; margin: 0; }
        .center-text { text-align: center; }
        .left-text { text-align: left; }
      </style>
      </head><body>`;

      const footer = "</body></html>";
      const sourceHTML = header + content + footer;

      const blob = new Blob(['\ufeff', sourceHTML], {
        type: 'application/msword',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `परिशिष्ट 1.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setEditMode(wasEditing);
    }, 200);
  };

  // Calculate second page data dynamically
  // ✅ Fixed function: returns only numeric total for private land
  const calculateSecondPageData = () => {
    let privateLandTotal = 0;

    data.forEach(item => {
      // if (item.aqr_area) {
      //   privateLandTotal += parseFloat(item.aqr_area) || 0;
      // }
      if (item.owners && item.owners.length > 0) {
        item.owners.forEach(owner => {
          if (owner.owner_aqr_area) {
            privateLandTotal += parseFloat(owner.owner_aqr_area) || 0;
          }
        });
      }
    });

    return privateLandTotal; // only number
  };


  const surveyTypes = ["सर्व्हे क्रमांक", "गट क्रमांक", "प्लॉट क्रमांक"];

  const surveyKramankData = data.filter(
    item => surveyTypes.includes(item.survey_type)
  );


  const gharKramankData = data.filter(
    item => item.survey_type === "घर क्रमांक."
  );

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-3">
          <button onClick={handlePrint} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <Printer size={18} />
            <span>Print</span>
          </button>
          <button onClick={handleDownloadWord} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
            <Download size={18} />
            <span>Download Word</span>
          </button>

          {/* Upload Signature - Conditional Rendering */}
          {/* {!previewUrl ? ( */}
          <button className="relative">
            <label className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 cursor-pointer">
              <Upload size={18} />
              <span>Upload Signature</span>
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={handleUploadedFile}
              />
            </label>
          </button>

          <button
            onClick={handleViewSignedPdf}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            <Printer size={18} />
            <span>View Signed PDF</span>
          </button>

        </div>
      </div>

      {previewUrl ? (
        // Show PDF directly if signed file exists
        <div className="w-full bg-white p-4 rounded-lg">
          <iframe
            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title="Anusuchi-1 PDF"
            width="100%"
            height="800px"
            style={{
              border: "none",
              borderRadius: "8px",
              boxShadow: "none",
              outline: "none",
              margin: "0",
              padding: "0"
            }}
            frameBorder="0"
            seamless
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="text-center mb-6">
            <div id="notice-content" style={{ margin: "10mm" }}>
              {loading ? (
                <div className="flex justify-center items-center h-96">
                  <p className="text-lg text-gray-600">Loading...</p>
                </div>
              ) : (
                <>
                  <h3 className="center-text font-semibold">परिशिष्ट - 1</h3>
                  <h3 className="center-text font-semibold">अनुसूची - 1</h3>
                  <p className="center-text" style={{ fontWeight: "bold" }}>
                    प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनींचा तपशील
                  </p>
                  <h3 className="center-text" style={{ fontWeight: "bold" }}>
                    प्रकल्प नाव: {data.projectName || proposalData?.project_name} | प्रस्ताव नाव: {proposalNo}
                  </h3>
                  {data.length > 0 && (
                    <p className="center-text" style={{ fontWeight: "bold" }}>
                      मौजे : {data[0].village_name} , तालुका : {data[0].taluka_name} , जिल्हा : {data[0].dist_name}
                    </p>
                  )}

                  {surveyKramankData.length > 0 && (
                    <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black" }}>
                      <thead>
                        <tr>
                          <th style={{ border: "1px solid black", padding: "6px" }}>अ.क्र.</th>
                          <th style={{ border: "1px solid black", padding: "6px", minWidth: "170px" }}>भूधारकाचे नाव</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>गट/सर्व्हे </th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>भूधारण पद्धती </th>
                          <th style={{ border: "1px solid black", padding: "6px", maxWidth: "120px" }}>७/१२ प्रमाणे/अभिलेखाप्रमाणे एकुण क्षेत्र ({data[0]?.unit_name})</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>हिस्सा क्षेत्र({data[0]?.unit_name})</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>संपादनाचे प्रस्तावित क्षेत्र ({data[0]?.unit_name})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...surveyKramankData]
                          .sort((a, b) => a.LandAcquisition_id - b.LandAcquisition_id)
                          .map((item, idx) => {
                            const hasOwners = item.owners && item.owners.length > 0;
                            const rowSpanValue = hasOwners ? item.owners.length : 1;

                            // Separate owners: those with individual aqr_area vs those sharing common_area
                            const ownersWithIndividualAqr = [];
                            const ownersWithCommonArea = [];

                            item.owners?.forEach(owner => {
                              const hasIndividualAqr = owner.owner_aqr_area && parseFloat(owner.owner_aqr_area) > 0;
                              if (hasIndividualAqr) {
                                ownersWithIndividualAqr.push(owner);
                              } else if (owner.common_area && parseFloat(owner.common_area) > 0) {
                                ownersWithCommonArea.push(owner);
                              }
                            });

                            return hasOwners ? (
                              <React.Fragment key={idx}>
                                {item.owners.map((owner, ownerIdx) => {
                                  const hasIndividualAqr = owner.owner_aqr_area && parseFloat(owner.owner_aqr_area) > 0;
                                  const hasCommonArea = owner.common_area && parseFloat(owner.common_area) > 0;

                                  // Check if this is the first owner with common_area (to show merged cell)
                                  const isFirstCommonAreaOwner = hasCommonArea &&
                                    ownerIdx === item.owners.findIndex(o =>
                                      o.common_area && parseFloat(o.common_area) > 0 &&
                                      (!o.owner_aqr_area || parseFloat(o.owner_aqr_area) === 0)
                                    );

                                  return (
                                    <tr key={`${idx}-owner-${ownerIdx}`}>
                                      {/* Sr No — only in first owner row */}
                                      {ownerIdx === 0 && (
                                        <td
                                          rowSpan={rowSpanValue}
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                          }}
                                        >
                                          {idx + 1}
                                        </td>
                                      )}

                                      {/* Owner Name */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          textAlign: "left",
                                          whiteSpace: "pre-line"
                                        }}
                                      >
                                        {owner.owner_name
                                          ?.split(",")
                                          .map((name, i, arr) =>
                                            i < arr.length - 1 ? name.trim() + "," : name.trim()
                                          )
                                          .join("\n")}
                                      </td>

                                      {/* Survey No — only in first owner row */}
                                      {ownerIdx === 0 && (
                                        <td
                                          rowSpan={rowSpanValue}
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            verticalAlign: "middle",
                                            textAlign: "center"
                                          }}
                                        >
                                          {item.survey_no}
                                        </td>
                                      )}

                                      {/* Land Holding Type — only in first owner row */}
                                      {ownerIdx === 0 && (
                                        <td
                                          rowSpan={rowSpanValue}
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            verticalAlign: "middle",
                                            textAlign: "center"
                                          }}
                                        >
                                          {item.land_holding_type || "-"}
                                        </td>
                                      )}

                                      {/* Total Area — only in first owner row */}
                                      {ownerIdx === 0 && (
                                        <td
                                          rowSpan={rowSpanValue}
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            verticalAlign: "middle",
                                            textAlign: "center"
                                          }}
                                        >
                                          {parseFloat(item.total_area || 0).toFixed(2)}
                                        </td>
                                      )}

                                      {/* Owner Total Area (हिस्सा क्षेत्र) */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          textAlign: "center",
                                          verticalAlign: "middle"
                                        }}
                                      >
                                        {parseFloat(owner.owner_total_area || 0).toFixed(2)}
                                      </td>

                                      {/* संपादनाचे प्रस्तावित क्षेत्र - Last Column */}
                                      {hasIndividualAqr ? (
                                        // Owner has individual acquire area - show it
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                          }}
                                        >
                                          {parseFloat(owner.owner_aqr_area).toFixed(2)}
                                        </td>
                                      ) : hasCommonArea && isFirstCommonAreaOwner ? (
                                        // First owner with common_area - show merged cell
                                        <td
                                          rowSpan={ownersWithCommonArea.length}
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                          }}
                                        >
                                          {parseFloat(owner.common_area).toFixed(2)}
                                        </td>
                                      ) : hasCommonArea && !isFirstCommonAreaOwner ? (
                                        // Other owners with common_area - don't render cell (already merged)
                                        null
                                      ) : (
                                        // No aqr_area and no common_area - show 0.00
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                          }}
                                        >
                                          0.00
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ) : null;
                          })}

                        {/* Total Row */}
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "right"
                            }}
                          >
                            एकुण
                          </td>
                          <td
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "center"
                            }}
                          >
                            {data.reduce(
                              (sum, item) =>
                                sum +
                                (item.owners?.reduce(
                                  (ownerSum, o) => ownerSum + Number(o.owner_total_area || 0),
                                  0
                                ) || 0),
                              0
                            ).toFixed(2)}
                          </td>
                          <td
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "center"
                            }}
                          >
                            {(
                              Math.round(
                                data.reduce(
                                  (sum, item) =>
                                    sum +
                                    (item.owners?.reduce(
                                      (ownerSum, o) => ownerSum + Number(o.owner_aqr_area || 0),
                                      0
                                    ) || 0),
                                  0
                                ) * 100
                              ) / 100
                            ).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}

                  {gharKramankData.length > 0 && (
                    <table className="mt-8" style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black" }}>
                      <thead>
                        <tr>
                          <th style={{ border: "1px solid black", padding: "6px" }}>अ.क्र.</th>
                          <th style={{ border: "1px solid black", padding: "6px", minWidth: "170px" }}>घर मालकाचे नाव</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>घर क्रमांक </th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>भूधारण पद्धती </th>
                          <th style={{ border: "1px solid black", padding: "6px", maxWidth: "120px" }}>७/१२ प्रमाणे/अभिलेखाप्रमाणे एकुण क्षेत्र (चौरस फूट)</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>हिस्सा क्षेत्र (चौरस फूट)</th>
                          <th style={{ border: "1px solid black", padding: "6px" }}>संपादनाचे प्रस्तावित क्षेत्र (चौरस फूट)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...gharKramankData]
                          .sort((a, b) => a.LandAcquisition_id - b.LandAcquisition_id)
                          .map((item, idx) => {
                            const hasOwners = item.owners && item.owners.length > 0;
                            const rowSpanValue = hasOwners ? item.owners.length : 1;

                            return hasOwners ? (
                              <React.Fragment key={idx}>
                                {item.owners.map((owner, ownerIdx) => (
                                  <tr key={`${idx}-owner-${ownerIdx}`}>
                                    {/* Sr No — only in first owner row */}
                                    {ownerIdx === 0 && (
                                      <td
                                        rowSpan={rowSpanValue}
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          textAlign: "center"
                                        }}
                                      >
                                        {idx + 1}
                                      </td>
                                    )}

                                    {/* Owner Name */}
                                    <td
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "left",
                                        whiteSpace: "pre-line"
                                      }}
                                    >
                                      {owner.owner_name
                                        ?.split(",")
                                        .map((name, i, arr) =>
                                          i < arr.length - 1 ? name.trim() + "," : name.trim()
                                        )
                                        .join("\n")}
                                    </td>

                                    {/* Survey No — only in first owner row */}
                                    {ownerIdx === 0 && (
                                      <td
                                        rowSpan={rowSpanValue}
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          verticalAlign: "center",
                                          textAlign: "center"
                                        }}
                                      >
                                        {item.survey_no}
                                      </td>
                                    )}

                                    {/* Land Holding Type — only in first owner row */}
                                    {ownerIdx === 0 && (
                                      <td
                                        rowSpan={rowSpanValue}
                                        style={{
                                          paddingBottom: "20px",
                                          border: "1px solid black",
                                          padding: "8px",
                                          verticalAlign: "center",
                                          textAlign: "center"
                                        }}
                                      >
                                        {item.land_holding_type || "-"}
                                      </td>
                                    )}

                                    {/* Total Area — only in first owner row */}
                                    {ownerIdx === 0 && (
                                      <td
                                        rowSpan={rowSpanValue}
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          verticalAlign: "center",
                                          textAlign: "center"
                                        }}
                                      >
                                        {parseFloat(item.total_area || 0).toFixed(2)}
                                      </td>
                                    )}

                                    {/* Owner Total Area */}
                                    <td
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        verticalAlign: "center"
                                      }}
                                    >
                                      {parseFloat(owner.owner_total_area || 0).toFixed(2)}
                                    </td>

                                    {/* Owner Aqr Area */}
                                    <td
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        verticalAlign: "center"
                                      }}
                                    >
                                      {parseFloat(owner.owner_aqr_area || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ) : null;
                          })}

                        {/* Total Row */}
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "right"
                            }}
                          >
                            एकुण
                          </td>
                          <td
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "center"
                            }}
                          >
                            {data.reduce(
                              (sum, item) =>
                                sum +
                                (item.owners?.reduce(
                                  (ownerSum, o) => ownerSum + Number(o.owner_total_area || 0),
                                  0
                                ) || 0),
                              0
                            ).toFixed(2)}
                          </td>
                          <td
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "center"
                            }}
                          >
                            {(
                              Math.round(
                                data.reduce(
                                  (sum, item) =>
                                    sum +
                                    (item.owners?.reduce(
                                      (ownerSum, o) => ownerSum + Number(o.owner_aqr_area || 0),
                                      0
                                    ) || 0),
                                  0
                                ) * 100
                              ) / 100
                            ).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}

                  <p
                    style={{
                      textAlign: "center",
                      marginTop: "20px",
                      fontSize: "small",
                    }}
                  ></p>

                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                    {/* Left side */}
                    <div style={{ textAlign: "left" }}>
                      <p>दिनांक: {formattedDate}</p>
                      <p>ठिकाण: {proposalData?.acquisition_incharge_address || "N/A"}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                    {/* Right side */}

                    <div style={{ textAlign: "center", marginTop: "100px", lineHeight: "1.5" }}>
                      <p> {InchargeName ? ` (${InchargeName})` : ""}</p>
                      <p> {InchargeDesignation ? ` ${InchargeDesignation}` : ""}</p>
                    </div>
                    <div style={{ textAlign: "center", lineHeight: "1.5", marginTop: "100px" }}>
                      <p> {regionalInchargeName ? ` (${regionalInchargeName})` : ""}</p>
                      <p> {regionalInchargeDesignation ? ` ${regionalInchargeDesignation}` : ""}</p>
                    </div>
                  </div>

                  {/* Divider only in UI */}
                  <hr className="my-6 border-t-2 border-gray-400 print:hide-in-print " />

                  {/* अनुसूची-२ */}
                  <div className="anushuchi-two mt-12" style={{ width: "100%", pageBreakBefore: "always", breakBefore: "page" }}>
                    <h3 className="center-text font-semibold">परिशिष्ट - 1</h3>
                    <h3 className="center-text font-semibold">अनुसूची - 2</h3>
                    <p className="center-text" style={{ fontWeight: "bold" }}>
                      प्रकल्पाच्या आखणीमध्ये समाविष्ट जमिनीचा गोषवारा
                    </p>
                    <h3 className="center-text" style={{ fontWeight: "bold" }}>
                      प्रकल्प नाव: {data.projectName || proposalData?.project_name} | प्रस्ताव नाव: {proposalNo}
                    </h3>
                    {data.length > 0 && (
                      <p className="center-text" style={{ fontWeight: "bold" }}>
                        मौजे : {data[0].village_name} , तालुका : {data[0].taluka_name} , जिल्हा : {data[0].dist_name}
                      </p>
                    )}

                    <table style={{ borderCollapse: "collapse", width: "100%", border: "1px solid black" }}>
                      <thead>
                        <tr>
                          <th style={{ border: "1px solid black", padding: "8px" }}>अ.क्र.</th>
                          <th style={{ border: "1px solid black", padding: "8px" }}>जमिनीचा प्रकार</th>
                          <th style={{ border: "1px solid black", padding: "8px" }}>भूसंपादन करावयाचे एकूण क्षेत्र ({data[0]?.unit_name || "sq.m"})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { type: "खाजगी जमीन", area: Number(calculateSecondPageData() || 0) },
                          { type: "सरकारी जमीन", area: 0.0 },
                          { type: "केंद्र/राज्य शासनाच्या अखत्यारीतील सार्वजनिक उपक्रमाकडील जमीन", area: 0.0 },
                          { type: "केंद्र/राज्य शासनाच्या विभागाकडील जमीन", area: 0.0 },
                          { type: "वन जमीन", area: 0.0 },
                          { type: "स्थानिक स्वराज्य संस्थाकडील जमीन", area: 0.0 },
                          { type: "अन्य जमीन", area: 0.0 },
                        ].map((row, i) => (
                          <tr key={i}>
                            <td style={{ border: "1px solid black", padding: "8px", textAlign: "center" }}>{i + 1}</td>
                            <td style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>{row.type}</td>
                            <td style={{ border: "1px solid black", padding: "8px", textAlign: "center" }}>
                              {Number(row.area || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={2} style={{ border: "1px solid black", padding: "8px", fontWeight: "bold", textAlign: "right" }}>एकूण :</td>
                          <td style={{ border: "1px solid black", padding: "8px", fontWeight: "bold", textAlign: "center" }}>{calculateSecondPageData().toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>

                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      {/* Left side */}
                      <div style={{ textAlign: "left" }}>
                        <p>दिनांक: {formattedDate}</p>
                        <p>ठिकाण: {proposalData?.acquisition_incharge_address || "N/A"}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                      {/* Right side */}

                      <div style={{ textAlign: "center", marginTop: "100px", lineHeight: "1.5" }}>
                        <p> {regionalInchargeName ? ` (${regionalInchargeName})` : ""}</p>
                        <p> {regionalInchargeDesignation ? ` ${regionalInchargeDesignation}` : ""}</p>
                      </div>
                      <div style={{ textAlign: "center", lineHeight: "1.5", marginTop: "100px" }}>
                        <p> {InchargeName ? ` (${InchargeName})` : ""}</p>
                        <p> {InchargeDesignation ? ` ${InchargeDesignation}` : ""}</p>
                      </div>
                    </div>
                  </div>

                  <p
                    style={{
                      textAlign: "center",
                      marginTop: "20px",
                      fontSize: "small",
                    }}
                  ></p>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {showSignaturePad && pdfForSigning && (
        <SignaturePad
          pdfFile={pdfForSigning}
          onSign={(signaturesArray) => {
            setShowSignaturePad(false);

            signDocument(pdfForSigning, signaturesArray).then(() => {
              setPdfForSigning(null);
            });
          }}
          onCancel={() => {
            setShowSignaturePad(false);
            setPdfForSigning(null);
          }}
        />
      )}

      {isViewerOpen && viewerFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg w-[80%] max-w-6xl h-[80%] relative">

            {/* Header */}
            <div className="flex justify-between items-center px-4 py-2 border-b">
              <h3 className="text-lg font-semibold">Document Viewer</h3>
              <button
                onClick={() => setIsViewerOpen(false)}
                className="text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* PDF Viewer */}
            <iframe
              src={viewerFile}
              title="Parishisht-1 Signed PDF"
              className="w-full h-full"
            />
          </div>
        </div>
      )}



    </div>
  );
};
export default HighwayAnusuchi1;